import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { Check, X, Eye, Pencil } from "lucide-react";
import { AppHeader } from "@/components/app/AppHeader";
import { LeaveDetailModal } from "@/components/hr/LeaveDetailModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useAuth, DEPARTMENT_LABELS, type Department } from "@/lib/auth-context";
import { listAllLeaves, reviewLeave, currentMonth, type LeaveApplication, type LeaveStatus } from "@/lib/leave";
import { todayIstIso, formatIst } from "@/lib/tz";

export const Route = createFileRoute("/_app/_hr/hr/leave")({
  component: LeavePage,
  head: () => ({ meta: [{ title: "Leave Management — HR" }] }),
});

const TYPE_LABEL: Record<string, string> = {
  paid_full_day: "Paid (Full Day)",
  paid_half_day: "Paid (Half Day)",
  unpaid: "Unpaid",
};

function LeavePage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const month = currentMonth();
  const [rejectRow, setRejectRow] = useState<LeaveApplication | null>(null);
  const [rejNote, setRejNote] = useState("");
  const [detailRow, setDetailRow] = useState<LeaveApplication | null>(null);
  const [detailEdit, setDetailEdit] = useState(false);

  const { data: leaves = [] } = useQuery({ queryKey: ["hr-leaves"], queryFn: listAllLeaves });

  const employeeIds = useMemo(() => Array.from(new Set(leaves.map((l) => l.employee_id))), [leaves]);

  const { data: profilesMap = {} } = useQuery({
    queryKey: ["hr-leaves-profiles", employeeIds.join(",")],
    enabled: employeeIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, employee_id, department")
        .in("id", employeeIds);
      if (error) throw error;
      const map: Record<string, { full_name: string; employee_id: string | null; department: string }> = {};
      (data ?? []).forEach((p: any) => { map[p.id] = p; });
      return map;
    },
  });

  const today = todayIstIso();
  const stats = useMemo(() => {
    const monthLeaves = leaves.filter((l) => l.from_date.slice(0, 7) === month);
    const pending = leaves.filter((l) => l.status === "pending").length;
    const approvedMonth = monthLeaves.filter((l) => l.status === "approved").length;
    const onLeaveToday = leaves.filter((l) => l.status === "approved" && l.from_date <= today && l.to_date >= today).length;
    const totalPaidUsed = monthLeaves
      .filter((l) => l.status === "approved" && l.leave_type !== "unpaid")
      .reduce((s, l) => s + Number(l.total_days), 0);
    return { pending, approvedMonth, onLeaveToday, totalPaidUsed };
  }, [leaves, month, today]);

  const approve = useMutation({
    mutationFn: (l: LeaveApplication) =>
      reviewLeave({ id: l.id, status: "approved", reviewed_by: user!.id }),
    onSuccess: () => {
      toast.success("Approved");
      qc.invalidateQueries({ queryKey: ["hr-leaves"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reject = useMutation({
    mutationFn: () => {
      if (!rejectRow) throw new Error("No row");
      if (rejNote.trim().length < 5) throw new Error("Note required");
      return reviewLeave({
        id: rejectRow.id,
        status: "rejected",
        rejection_note: rejNote.trim(),
        reviewed_by: user!.id,
      });
    },
    onSuccess: () => {
      toast.success("Rejected");
      qc.invalidateQueries({ queryKey: ["hr-leaves"] });
      setRejectRow(null);
      setRejNote("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const StatusPill = ({ s, note }: { s: LeaveStatus; note: string | null }) => {
    if (s === "approved") return <Badge className="bg-green-600 hover:bg-green-700">Approved</Badge>;
    if (s === "rejected") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild><Badge variant="destructive" className="cursor-help">Rejected</Badge></TooltipTrigger>
            <TooltipContent className="max-w-xs">{note ?? "No reason given"}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    return <Badge className="bg-amber-500 hover:bg-amber-600">Pending</Badge>;
  };

  const renderTable = (rows: LeaveApplication[]) => (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Emp ID</TableHead>
              <TableHead>Dept</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Days</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground">No leave applications</TableCell></TableRow>
            ) : rows.map((l) => {
              const p = profilesMap[l.employee_id];
              return (
                <TableRow key={l.id}>
                  <TableCell className="font-medium">
                    <button className="hover:underline text-left" onClick={() => { setDetailRow(l); setDetailEdit(false); }}>
                      {p?.full_name ?? "—"}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{p?.employee_id ?? "—"}</TableCell>
                  <TableCell className="text-xs">{p?.department ? (DEPARTMENT_LABELS[p.department as Department] ?? p.department) : "—"}</TableCell>
                  <TableCell className="text-xs">{TYPE_LABEL[l.leave_type]}</TableCell>
                  <TableCell className="text-xs">{formatIst(l.from_date, "MMM d")}</TableCell>
                  <TableCell className="text-xs">{formatIst(l.to_date, "MMM d")}</TableCell>
                  <TableCell className="text-xs">{Number(l.total_days).toFixed(1)}</TableCell>
                  <TableCell className="max-w-[220px] truncate text-xs">{l.reason}</TableCell>
                  <TableCell className="text-xs">{formatIst(l.created_at, "MMM d")}</TableCell>
                  <TableCell><StatusPill s={l.status} note={l.rejection_note} /></TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" className="h-7" title="View"
                        onClick={() => { setDetailRow(l); setDetailEdit(false); }}>
                        <Eye className="h-3 w-3" />
                      </Button>
                      {l.status === "pending" && (
                        <>
                          <Button size="sm" variant="outline" className="h-7" title="Edit"
                            onClick={() => { setDetailRow(l); setDetailEdit(true); }}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-green-700 border-green-300 hover:bg-green-50"
                            onClick={() => approve.mutate(l)} disabled={approve.isPending}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-red-700 border-red-300 hover:bg-red-50"
                            onClick={() => { setRejectRow(l); setRejNote(""); }}>
                            <X className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  const monthLabel = new Date(month + "-01").toLocaleString("en-US", { month: "long", year: "numeric" });
  const pending = leaves.filter((l) => l.status === "pending");

  return (
    <>
      <AppHeader subtitle={`${monthLabel} · ${stats.pending} pending · ${stats.approvedMonth} approved this month`} />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Pending Approval" value={String(stats.pending)} />
          <StatCard label="Approved This Month" value={String(stats.approvedMonth)} />
          <StatCard label="On Leave Today" value={String(stats.onLeaveToday)} />
          <StatCard label="Paid Leaves Used (Co.)" value={stats.totalPaidUsed.toFixed(1)} />
        </div>

        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending">Pending ({pending.length})</TabsTrigger>
            <TabsTrigger value="all">All Requests</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="mt-3">{renderTable(pending)}</TabsContent>
          <TabsContent value="all" className="mt-3">{renderTable(leaves)}</TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!rejectRow} onOpenChange={(o) => !o && setRejectRow(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Leave Request</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">Provide a reason. The employee will see this note.</p>
            <Textarea rows={4} value={rejNote} onChange={(e) => setRejNote(e.target.value)} placeholder="Reason for rejection..." />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectRow(null)}>Cancel</Button>
            <Button onClick={() => reject.mutate()} disabled={reject.isPending || rejNote.trim().length < 5} className="bg-red-600 hover:bg-red-700 text-white">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LeaveDetailModal
        leave={detailRow}
        employeeName={detailRow ? (profilesMap[detailRow.employee_id]?.full_name ?? "—") : ""}
        employeeEmpId={detailRow ? (profilesMap[detailRow.employee_id]?.employee_id ?? null) : null}
        employeeDept={detailRow ? (DEPARTMENT_LABELS[(profilesMap[detailRow.employee_id]?.department ?? "") as Department] ?? (profilesMap[detailRow.employee_id]?.department ?? "")) : ""}
        open={!!detailRow}
        onOpenChange={(v) => { if (!v) { setDetailRow(null); setDetailEdit(false); } }}
        currentUserId={user?.id ?? ""}
        isHr
        startInEdit={detailEdit}
      />
    </>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-[11px] text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold mt-1">{value}</div>
      </CardContent>
    </Card>
  );
}
